import moment from 'moment-timezone';
import { TimeSpanEnum } from '../enums/timeSpan.enum';
import i18n from '../locales/i18n';
import compact from 'lodash/compact';
import Vue from 'vue';
import { dateFormatTypes } from '../enums/dateFormatTypes.js';
import { dateNameFormats } from '../enums/dateNameFormats.js';
import { weekdayEnum } from '../enums/weekdayEnum';

export class DateTimeUtil {
  /**
   * Converts a date string to a specified format.
   * @param date - The date string to be converted.
   * @param format - The desired format for the date.
   * @returns The formatted date string.
   */
  static formatDate(date: string | Date, format: string): string {
    return moment(date).format(format);
  }

  /**
   * Converts a date string to a specified format in UTC.
   * @param date - The date string to be converted.
   * @param format - The desired format for the date.
   * @returns The formatted date string.
   */
  static formatUtcDate(date: string | Date, format: string): string {
    return moment(date).utc().format(format);
  }

  /**
   * Get the week day by iso weekday
   * Sunday is represented as 0 and Saturday as 6
   * @param isoWeekday
   * @param format
   * @returns the week day
   */
  static getWeekday(isoWeekday: number, format: string = 'dddd'): string {
    return moment().isoWeekday(isoWeekday).format(format);
  }

  /**
   * Get week number of given date
   * @param date
   * @return week number
   */
  static getWeekNumber(date: string | Date): number {
    return moment(date).week();
  }

  /**
   * If week is even or odd using local time
   * @param date
   */
  static isEvenWeek(date: string | Date): boolean {
    return moment(date).week() % 2 === 0;
  }

  /**
   * Calculates the difference between two dates in the specified unit.
   * @param date1 - The first date.
   * @param date2 - The second date.
   * @param unit - The unit of time to measure the difference (e.g., 'days', 'months', 'years').
   * @returns The difference between the two dates in the specified unit.
   */
  static dateDifference(date1: string | Date, date2: string | Date, unit: moment.unitOfTime.Diff = 'days'): number {
    return moment(date1).diff(moment(date2), unit);
  }

  /**
   * Adds a specified amount of time to a date.
   * @param date - The original date.
   * @param amount - The amount of time to add.
   * @param unit - The unit of time to add (e.g., 'days', 'months', 'years').
   * @returns A new Date object with the added time.
   */
  static addTime(date: string | Date, amount: number, unit: moment.unitOfTime.DurationConstructor): Date {
    return moment(date).add(amount, unit).toDate();
  }

  /**
   * Subtracts a specified amount of time from a date.
   * @param date - The original date.
   * @param amount - The amount of time to subtract.
   * @param unit - The unit of time to subtract (e.g., 'days', 'months', 'years').
   * @returns A new Date object with the subtracted time.
   */
  static subtractTime(date: string | Date, amount: number, unit: moment.unitOfTime.DurationConstructor): Date {
    return moment(date).subtract(amount, unit).toDate();
  }

  /**
   * Subtracts a specified amount of time from a formatted date
   * @param date - The original date.
   * @param format - The desired format for the date.
   * @param amount - The amount of time to subtract.
   * @param unit - The unit of time to subtract.
   * @returns A new formatted date string with the subtracted time.
   */
  static substractTimeWithFormat(
    date: string | Date,
    format: string,
    amount: number,
    unit: moment.unitOfTime.DurationConstructor
  ): string {
    return moment(date, format).subtract(amount, unit).format(format);
  }

  /**
   * Checks if a date is valid.
   * @param date - The date string or object to be validated.
   * @returns True if the date is valid, false otherwise.
   */
  static isValidDate(date: string | Date): boolean {
    return moment(date).isValid();
  }

  /**
   * Returns the current date and time as a formatted string.
   * @param format - The desired format for the date and time.
   * @returns The current date and time as a formatted string.
   */
  static getCurrentDateTime(format: string = ''): string {
    return moment().format(format);
  }

  /**
   * Gets the start of a unit of time for a given date.
   * @param date - The original date.
   * @param unit - The unit of time to get the start of (e.g., 'day', 'month', 'year').
   * @returns A new Date object representing the start of the specified unit of time.
   */
  static getStartOf(date: string | Date, unit: moment.unitOfTime.StartOf): Date {
    return moment(date).startOf(unit).toDate();
  }

  /**
   * Gets the end of a unit of time for a given date.
   * @param date - The original date.
   * @param unit - The unit of time to get the end of (e.g., 'day', 'month', 'year').
   * @returns A new Date object representing the end of the specified unit of time.
   */
  static getEndOf(date: string | Date, unit: moment.unitOfTime.StartOf): Date {
    return moment(date).endOf(unit).toDate();
  }

  /**
   * Gets the difference between the current time and a given date in the specified unit.
   * @param date - The date to compare with the current time.
   * @param unit - The unit of time to measure the difference (e.g., 'days', 'hours').
   * @returns The difference between the current time and the given date in the specified unit.
   */
  static getTimeFromNow(date: string | Date, unit: moment.unitOfTime.Diff = 'days'): number {
    return moment().diff(moment(date), unit);
  }

  /**
   * Gets the difference between a given date and the current time in the specified unit.
   * @param date - The date to compare with the current time.
   * @param unit - The unit of time to measure the difference (e.g., 'days', 'hours').
   * @returns The difference between the given date and the current time in the specified unit.
   */
  static getTimeUntilNow(date: string | Date, unit: moment.unitOfTime.Diff = 'days'): number {
    return moment(date).diff(moment(), unit);
  }

  /**
   * Checks if a date is the same as or before another date.
   * @param date1 - The first date.
   * @param date2 - The second date to compare against.
   * @param unit - The unit of time to measure the difference
   * @returns True if date1 is the same as or before date2, false otherwise.
   */
  static isSameOrBefore(date1: string | Date, date2: string | Date, unit: moment.unitOfTime.StartOf = 'date'): boolean {
    return moment(date1).isSameOrBefore(moment(date2), unit);
  }

  /**
   * Checks if a date is the same as or after another date.
   * @param date1 - The first date.
   * @param date2 - The second date to compare against.
   * @param unit - The unit of time to measure the difference
   * @returns True if date1 is the same as or after date2, false otherwise.
   */
  static isSameOrAfter(date1: string | Date, date2: string | Date, unit: moment.unitOfTime.StartOf = 'date'): boolean {
    return moment(date1).isSameOrAfter(moment(date2), unit);
  }

  /**
   * Checks if a date is the same as another date.
   * @param date1 - The first date.
   * @param date2 - The second date to compare against.
   * @param unit - The unit of time to measure the difference
   * @returns True if date1 is the same as date2, false otherwise.
   */
  static isSame(date1: string | Date, date2: string | Date, unit: moment.unitOfTime.StartOf = 'date'): boolean {
    return moment(date1).isSame(moment(date2), unit);
  }

  /**
   * Checks if a date is between two other dates.
   * @param date - The date to check.
   * @param startDate - The start date.
   * @param endDate - The end date.
   * @param inclusivity - Whether the bounds are inclusive ('[)' or '()').
   * @returns True if the date is between startDate and endDate, false otherwise.
   */
  static isBetween(
    date: string | Date,
    startDate: string | Date,
    endDate: string | Date,
    inclusivity: '()' | '[)' | '(]' | '[]' = '[]'
  ): boolean {
    return moment(date).isBetween(moment(startDate), moment(endDate), undefined, inclusivity);
  }

  /**
   * Converts a date to UTC.
   * @param date - The date to convert.
   * @returns A new Date object representing the UTC time.
   */
  static convertToUTC(date: string | Date): Date {
    return moment(date).utc().toDate();
  }

  /**
   * Converts a UTC date to local time.
   * @param date - The UTC date to convert.
   * @returns A new Date object representing the local time.
   */
  static convertFromUTC(date: string | Date): Date {
    return moment.utc(date).local().toDate();
  }

  /**
   * Add time span to the given date
   * @param date
   * @param timeSpan
   * @return A new Date object with added time span
   */
  static addTimeSpan(date: string | Date, timeSpan: TimeSpanEnum): Date {
    let result = new Date(date);
    switch (timeSpan) {
      case TimeSpanEnum.TWO_WEEKS:
        result = this.addTime(date, 2, 'week');
        break;
      case TimeSpanEnum.ONE_MONTH:
        result = this.addTime(date, 1, 'month');
        break;
      case TimeSpanEnum.THREE_MONTHS:
        result = this.addTime(date, 3, 'month');
        break;
    }
    return result;
  }

  /**
   *
   * @param date
   * @return A non past date based on the given date
   */
  static getFutureOrToday(date: string | Date): Date {
    const isFutureOrToday = moment(date).isSameOrAfter(moment(), 'day');
    if (isFutureOrToday) {
      return new Date(date);
    }
    return new Date();
  }

  /**
   * Get formatted time range, optionally in UTC
   * @param startDate
   * @param endDate
   * @param inUTC - if true, interpret dates in UTC
   */
  static formatTimeRange(startDate: string | Date, endDate: string | Date, inUTC: boolean = false): string {
    const dateFormat = 'ddd. DD. MMM. YYYY';
    const timeFormat = 'HH.mm';

    const startMoment = inUTC ? moment.utc(startDate) : moment(startDate);
    const endMoment = inUTC ? moment.utc(endDate) : moment(endDate);

    const formattedStartDate = startMoment.format(dateFormat).toLowerCase();
    const formattedStartTime = startMoment.format(timeFormat);
    const formattedEndDate = endMoment.format(dateFormat).toLowerCase();
    const formattedEndTime = endMoment.format(timeFormat);

    if (startMoment.isSame(endMoment, 'day')) {
      return `${formattedStartDate} \u00A0 · \u00A0 ${formattedStartTime} - ${formattedEndTime}`;
    } else {
      return `${formattedStartDate} \u00A0 · \u00A0 ${formattedStartTime} - ${formattedEndDate} \u00A0 · \u00A0 ${formattedEndTime}`;
    }
  }


  private static readonly ALL_DAY_START_TIME = '00:00:00';
  private static readonly ALL_DAY_END_TIME = '23:59:00';

  /**
   * Check if a date range represents an all-day event (00:00:00 to 23:59:00 UTC)
   */
  static isAllDayUtc(startDate: string | Date, endDate: string | Date): boolean {
    const startMoment = moment.utc(startDate);
    const endMoment = moment.utc(endDate);
    return startMoment.format('HH:mm:ss') === DateTimeUtil.ALL_DAY_START_TIME
      && endMoment.format('HH:mm:ss') === DateTimeUtil.ALL_DAY_END_TIME;
  }

  /**
   * Get formatted date range
   * @param startDate
   * @param endDate
   */
  static formatDateRange(startDate: string | Date, endDate: string | Date, inUTC: boolean = false): string {
    const dateFormat = 'ddd. DD. MMM. YYYY';

    const startMoment = inUTC ? moment.utc(startDate) : moment(startDate);
    const endMoment = inUTC ? moment.utc(endDate) : moment(endDate);

    const formattedStartDate = startMoment.format(dateFormat).toLowerCase();
    const formattedEndDate = endMoment.format(dateFormat).toLowerCase();

    if (startMoment.isSame(endMoment, 'day')) {
      return `${formattedStartDate}`;
    } else {
      return `${formattedStartDate} - ${formattedEndDate}`;
    }
  }

  /**
   * Calculate duration between start and end dates
   * @param startDate
   * @param endDate
   */
  static calculateDuration(startDate: string | Date, endDate: string | Date): string {
    const startMoment = moment(startDate);
    const endMoment = moment(endDate);
    const duration = moment.duration(endMoment.diff(startMoment));

    const days = duration.days();
    const hours = duration.hours();
    const minutes = duration.minutes();

    const parts: string[] = [];
    if (days > 0) {
      parts.push(i18n.tc('label.number_of_days', days));
    }
    if (hours > 0) {
      parts.push(i18n.tc('label.number_of_hours', hours));
    }
    if (minutes > 0) {
      parts.push(i18n.tc('label.number_of_minutes', minutes));
    }

    const lastPart = parts.pop();
    return compact([parts.join(', '), lastPart]).join(` ${i18n.t('label.and')} `);
  }

  static isSameTime(startDate: string | Date, endDate: string | Date) {
    const startMoment = moment(startDate);
    const endMoment = moment(endDate);
    const startHours = startMoment.hours();
    const startMinutes = startMoment.minutes();
    const endHours = endMoment.hours();
    const endMinutes = endMoment.minutes();
    return startHours === endHours && startMinutes === endMinutes;
  }

  /**
   * Get date difference in specified unit
   * @param startDate
   * @param endDate
   * @param unit
   */
  static getDateDifference(
    startDate: string | Date,
    endDate: string | Date,
    unit: moment.unitOfTime.Base = 'minutes'
  ): number {
    const startMoment = moment(startDate);
    const endMoment = moment(endDate);
    return endMoment.diff(startMoment, unit);
  }

  /**
   * Get date difference in minutes, clipped to the day of the given reference day.
   * The duration is bounded by the start and end of the reference day, so a
   * midnight-crossing event only counts the portion that falls on that day.
   * @param startDate
   * @param endDate
   * @param referenceDay
   */
  static getDateDifferenceOnDay(startDate: string | Date, endDate: string | Date, referenceDay: string | Date): number {
    const dayStart = moment(referenceDay).startOf('day');
    const nextDayStart = dayStart.clone().add(1, 'day');
    const startMoment = moment(startDate);
    const endMoment = moment(endDate);
    const clippedStart = startMoment.isBefore(dayStart) ? dayStart : startMoment;
    const clippedEnd = endMoment.isAfter(nextDayStart) ? nextDayStart : endMoment;
    return clippedEnd.diff(clippedStart, 'minutes');
  }

  /**
   * Gets the name of a day of the week based on its ISO number (1-7, where 1 is Monday and 7 is Sunday)
   * @param date - The ISO day number (1-7)
   * @returns The translated name of the day
   */
  static isoDateNumberToName(date: number): string {
    switch (date) {
      case 1:
        return Vue.prototype.$getText('DATETIME_MONDAY');
      case 2:
        return Vue.prototype.$getText('DATETIME_TUESDAY');
      case 3:
        return Vue.prototype.$getText('DATETIME_WEDNESDAY');
      case 4:
        return Vue.prototype.$getText('DATETIME_THURSDAY');
      case 5:
        return Vue.prototype.$getText('DATETIME_FRIDAY');
      case 6:
        return Vue.prototype.$getText('DATETIME_SATURDAY');
      case 7:
        return Vue.prototype.$getText('DATETIME_SUNDAY');
      default:
        return '';
    }
  }

  /**
   * Gets the name of a day of the week based on its number (0-6, where 0 is Sunday and 6 is Saturday)
   * @param date - The day number (0-6)
   * @returns The translated name of the day
   */
  static dateNumberToName(date: number): string {
    switch (date) {
      case 0:
        return Vue.prototype.$getText('CALENDAR_LABEL_EVENT_SUNDAY');
      case 1:
        return Vue.prototype.$getText('CALENDAR_LABEL_EVENT_MONDAY');
      case 2:
        return Vue.prototype.$getText('CALENDAR_LABEL_EVENT_TUESDAY');
      case 3:
        return Vue.prototype.$getText('CALENDAR_LABEL_EVENT_WEDNESDAY');
      case 4:
        return Vue.prototype.$getText('CALENDAR_LABEL_EVENT_THURSDAY');
      case 5:
        return Vue.prototype.$getText('CALENDAR_LABEL_EVENT_FRIDAY');
      case 6:
        return Vue.prototype.$getText('CALENDAR_LABEL_EVENT_SATURDAY');
      default:
        return '';
    }
  }

  /**
   * Formats a date in a short format
   * @deprecated This method is deprecated, use formatDate instead
   * @param date The date to format
   * @param year Whether to include the year
   * @param time Whether to include the time
   * @param utc Whether to use UTC time
   * @returns The formatted date string
   */
  static shortDate(date: Date | string | number, year = false, time = false, utc = false): string {
    let format = 'Do MMM[.]';
    if (year) {
      format += ' YYYY';
      if (time) {
        format += ' [kl.] LT';
      }
    }
    const momentDate = moment(date);
    const outDate = utc ? momentDate.utc() : momentDate;
    return outDate.format(format);
  }

  /**
   * Formats a date in a long format
   * @deprecated This method is deprecated, use formatDate instead
   * @param date The date to format
   * @param year Whether to include the year
   * @param time Whether to include the time
   * @param utc Whether to use UTC time
   * @returns The formatted date string
   */
  static longDate(date: Date | string | number, year = false, time = false, utc = false): string {
    let format = 'dddd[,] D. MMM[.]';
    if (year) {
      format += ' YYYY';
    }
    if (time) {
      format += ' [kl.] LT';
    }
    const outDate = utc ? moment(date).utc() : moment(date);
    return outDate.format(format);
  }

  /**
   * Formats a date in the selected format
   * @deprecated This method is deprecated, use formatDate instead
   * @param format
   * @param date The date to format
   * @returns The formatted date string
   */
  static date(format: any, date = moment()): string {
    const momentDate = moment(date);

    switch (format) {
      case dateFormatTypes.shortWeekDateName:
        return dateNameFormats.dateOfWeek[momentDate.isoWeekday()];
      case dateFormatTypes.monthDay:
        return momentDate.format('DD');
      case dateFormatTypes.dayMonthYear:
        return momentDate.format('DD-MM-YYYY');
      case dateFormatTypes.dayMonthYearTime:
        return momentDate.format('DD-MM-YYYY HH:mm');
      case dateFormatTypes.shortDate:
        return momentDate.format('ddd - DD');
      default:
        return momentDate.format(format);
    }
  }

  /**
   * Check if date is strictly before another date
   * @param date1 - The first date.
   * @param date2 - The second date to compare against.
   * @param unit
   */
  static isBefore(date1: string | Date, date2: string | Date, unit: moment.unitOfTime.StartOf = 'day'): boolean {
    return moment(date1).isBefore(moment(date2), unit);
  }

  /**
   * Check if date is strictly after another date
   * @param date1 - The first date.
   * @param date2 - The second date to compare against.
   * @param unit
   */
  static isAfter(date1: string | Date, date2: string | Date, unit: moment.unitOfTime.StartOf = 'day'): boolean {
    return moment(date1).isAfter(moment(date2), unit);
  }

  /**
   * Check if date is Weekend
   * @param date
   */
  static isWeekend(date: string | Date): boolean {
    const day = moment(date).day();
    return day === weekdayEnum.SUNDAY || day === weekdayEnum.SATURDAY;
  }

  /**
   * Checks if the date range includes at least one working day.
   * @param startDate
   * @param endDate
   */
  static hasWorkingDay(startDate: string | null, endDate: string | null): boolean {
    if (!startDate || !endDate) {
      return true;
    }

    const isStartWeekend = this.isWeekend(startDate);
    const isEndWeekend = this.isWeekend(endDate);

    if (isStartWeekend && isEndWeekend) {
      const diffDays = this.dateDifference(endDate, startDate, 'days');
      return diffDays >= 3;
    }

    return true;
  }

  /**
   * Return tomorrow of locale date time at 0 AM
   */
  static getStartOfTomorrow(): string {
    return moment().add(1, 'day').startOf('day').format();
  }
}
